import React, { useEffect, useState } from 'react';
import { FaArchive, FaPlus, FaSyncAlt, FaUserFriends } from 'react-icons/fa';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import { visitorService } from '../services/visitor.service';
import type { Visitor, VisitorFormData } from '../types/visitor';

const emptyForm: VisitorFormData = {
  name: '',
  visitDate: new Date().toISOString().split('T')[0],
  phoneNumber: '',
  email: '',
  guide: '',
  memo: '',
};

export const VisitorsPage: React.FC = () => {
  const { addNotification } = useNotification();
  const { canWrite } = usePermissions();
  const canEdit = canWrite('visitors');
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [form, setForm] = useState<VisitorFormData>(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setVisitors(await visitorService.getAllVisitors());
    } catch (error) {
      console.error('방문자 목록 로딩 오류:', error);
      addNotification('방문자 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      addNotification('방문자 이름을 입력해주세요.', 'error');
      return;
    }
    setSaving(true);
    try {
      await visitorService.addVisitor({ ...form, name: form.name.trim() });
      addNotification('방문자가 등록되었습니다.', 'success');
      setForm({ ...emptyForm, visitDate: form.visitDate });
      setShowForm(false);
      await load();
    } catch (error) {
      console.error('방문자 저장 오류:', error);
      addNotification('방문자 저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (visitor: Visitor) => {
    if (!window.confirm(`${visitor.name} 방문자를 보관 처리할까요?`)) return;
    try {
      await visitorService.archiveVisitor(visitor.id);
      addNotification('방문자 기록을 보관했습니다.', 'success');
      await load();
    } catch (error) {
      console.error('방문자 보관 오류:', error);
      addNotification('방문자 보관 처리에 실패했습니다.', 'error');
    }
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center">
          <FaUserFriends className="text-3xl text-emerald-600 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-700 dark:text-gray-200">방문자관리</h1>
            <p className="text-gray-500 dark:text-gray-400">새가족 등록 전 방문 기록을 따로 보관합니다.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void load()} className="px-3 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700" title="새로고침"><FaSyncAlt /></button>
          {canEdit && <button onClick={() => setShowForm((visible) => !visible)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2"><FaPlus />방문자 등록</button>}
        </div>
      </div>

      {showForm && canEdit && (
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-200">이름<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" /></label>
          <label className="text-sm text-gray-700 dark:text-gray-200">방문일<input type="date" required value={form.visitDate} onChange={(e) => setForm({ ...form, visitDate: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" /></label>
          <label className="text-sm text-gray-700 dark:text-gray-200">연락처<input value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" /></label>
          <label className="text-sm text-gray-700 dark:text-gray-200">이메일<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" /></label>
          <label className="text-sm text-gray-700 dark:text-gray-200">인도자<input value={form.guide} onChange={(e) => setForm({ ...form, guide: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" /></label>
          <label className="text-sm text-gray-700 dark:text-gray-200 md:col-span-2">메모<textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={2} /></label>
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg">취소</button><button disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50">{saving ? '저장 중...' : '저장'}</button></div>
        </form>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-500">방문자 목록을 불러오는 중...</div> : visitors.length === 0 ? <div className="p-10 text-center text-gray-500">등록된 방문자가 없습니다.</div> : <div className="overflow-x-auto"><table className="min-w-full"><thead className="bg-gray-50 dark:bg-gray-700"><tr><th className="px-4 py-3 text-left">방문일</th><th className="px-4 py-3 text-left">이름</th><th className="px-4 py-3 text-left">연락처</th><th className="px-4 py-3 text-left">인도자</th><th className="px-4 py-3 text-left">출처</th><th className="px-4 py-3 text-right">상태</th></tr></thead><tbody>{visitors.map((visitor) => <tr key={visitor.id} className="border-t dark:border-gray-700"><td className="px-4 py-3">{visitor.visitDate}</td><td className="px-4 py-3 font-medium">{visitor.name}</td><td className="px-4 py-3">{visitor.phoneNumber || '-'}</td><td className="px-4 py-3">{visitor.guide || '-'}</td><td className="px-4 py-3">{visitor.source === 'google-sheet' ? 'Google Sheet' : '직접 입력'}</td><td className="px-4 py-3 text-right">{visitor.status === 'archived' ? <span className="text-gray-400">보관</span> : <button onClick={() => void archive(visitor)} className="text-gray-500 hover:text-amber-600" title="보관"><FaArchive /></button>}</td></tr>)}</tbody></table></div>}
      </div>
    </div>
  );
};
